import React, { useState, useEffect, useRef, useLayoutEffect } from 'react';
import { ChevronLeft, ChevronRight, X } from 'lucide-react';
import {
  shouldShowPlayerTour,
  markPlayerTourDone,
  KEYS,
} from '../../lib/onboardingState';
import { capture } from '../../lib/posthog';
import { useUIStore } from '../../stores/uiStore';
import './PlayerTour.css';

const STEPS = [
  {
    target: '[data-tour="search-bar"]',
    title: 'Recherche',
    content: "Trouvez n'importe quelle ambiance en quelques mots.",
    placement: 'bottom',
  },
  {
    target: '[data-tour="sidebar-nav"]',
    title: 'Navigation',
    content: 'Accueil, Explorer, Bibliothèque — tout est là.',
    placement: 'right',
  },
  {
    target: '[data-tour="resume-listening"]',
    title: 'Reprise',
    content: 'Reprenez votre dernière session en un clic.',
    placement: 'top',
  },
  {
    target: '[data-tour="player-controls"]',
    title: 'Contrôles',
    content: 'Lecture, like, queue, plein écran — tout en bas.',
    placement: 'top',
  },
];

const TOOLTIP_W = 300;
const TOOLTIP_GAP = 14;

function clampPos(pos, w, h) {
  const margin = 12;
  return {
    left: Math.max(margin, Math.min(pos.left, window.innerWidth - w - margin)),
    top: Math.max(margin, Math.min(pos.top, window.innerHeight - h - margin)),
  };
}

function positionFor(rect, placement, tooltipH) {
  const cx = rect.left + rect.width / 2;
  const cy = rect.top + rect.height / 2;
  switch (placement) {
    case 'top':
      return clampPos(
        { left: cx - TOOLTIP_W / 2, top: rect.top - tooltipH - TOOLTIP_GAP },
        TOOLTIP_W,
        tooltipH
      );
    case 'bottom':
      return clampPos(
        { left: cx - TOOLTIP_W / 2, top: rect.bottom + TOOLTIP_GAP },
        TOOLTIP_W,
        tooltipH
      );
    case 'left':
      return clampPos(
        { left: rect.left - TOOLTIP_W - TOOLTIP_GAP, top: cy - tooltipH / 2 },
        TOOLTIP_W,
        tooltipH
      );
    case 'right':
    default:
      return clampPos(
        { left: rect.right + TOOLTIP_GAP, top: cy - tooltipH / 2 },
        TOOLTIP_W,
        tooltipH
      );
  }
}

function waitForAllTargets(maxWaitMs = 8000, intervalMs = 200) {
  return new Promise((resolve) => {
    const started = Date.now();
    const tick = () => {
      const ok = STEPS.every((s) => document.querySelector(s.target));
      if (ok) return resolve(true);
      if (Date.now() - started > maxWaitMs) return resolve(false);
      setTimeout(tick, intervalMs);
    };
    tick();
  });
}

export function PlayerTour() {
  const startPlayerTour = useUIStore((s) => s.startPlayerTour);
  const setStartPlayerTour = useUIStore((s) => s.setStartPlayerTour);

  const [active, setActive] = useState(false);
  const [stepIdx, setStepIdx] = useState(0);
  const [rect, setRect] = useState(null);
  const [tooltipPos, setTooltipPos] = useState({ left: 0, top: 0 });
  const tooltipRef = useRef(null);
  const triedRef = useRef(false);

  // Decide whether to start the tour.
  useEffect(() => {
    if (active) return;
    if (!shouldShowPlayerTour()) return;
    if (triedRef.current) return;

    const onboardingDone = !!localStorage.getItem(KEYS.ONBOARDED);
    if (!onboardingDone && !startPlayerTour) return;

    triedRef.current = true;
    let cancelled = false;
    waitForAllTargets().then((ok) => {
      if (cancelled) return;
      if (!ok) {
        triedRef.current = false;
        return;
      }
      setStepIdx(0);
      setActive(true);
      capture('tour_started', { tour: 'player' });
    });

    return () => {
      cancelled = true;
    };
  }, [startPlayerTour, active]);

  // Compute target rect + tooltip position whenever step changes / on resize / scroll.
  useLayoutEffect(() => {
    if (!active) return;
    const step = STEPS[stepIdx];
    if (!step) return;

    const measure = () => {
      const el = document.querySelector(step.target);
      if (!el) {
        // target disappeared — skip to next or end
        if (stepIdx + 1 < STEPS.length) setStepIdx((i) => i + 1);
        else finish('finished');
        return;
      }
      const r = el.getBoundingClientRect();
      setRect({
        left: r.left,
        top: r.top,
        right: r.right,
        bottom: r.bottom,
        width: r.width,
        height: r.height,
      });

      // Scroll element into view if needed.
      if (r.top < 80 || r.bottom > window.innerHeight - 80) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    };

    measure();
    const onResize = () => measure();
    window.addEventListener('resize', onResize);
    window.addEventListener('scroll', onResize, true);
    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('scroll', onResize, true);
    };
    // eslint-disable-next-line -- finish is stable in practice
  }, [active, stepIdx]);

  // Reposition tooltip after its own size is known.
  useLayoutEffect(() => {
    if (!active || !rect) return;
    const step = STEPS[stepIdx];
    const h = tooltipRef.current?.offsetHeight ?? 140;
    setTooltipPos(positionFor(rect, step.placement, h));
  }, [active, rect, stepIdx]);

  function finish(status) {
    setActive(false);
    setStartPlayerTour(false);
    markPlayerTourDone();
    capture(status === 'skipped' ? 'tour_skipped' : 'tour_completed', {
      tour: 'player',
      atStep: stepIdx,
    });
  }

  if (!active) return null;
  const step = STEPS[stepIdx];
  if (!step || !rect) return null;

  const isLast = stepIdx === STEPS.length - 1;
  const isFirst = stepIdx === 0;

  return (
    <div
      className="ptour-root"
      role="dialog"
      aria-modal="true"
      aria-labelledby="ptour-title"
    >
      <div
        className="ptour-overlay"
        onClick={() => finish('skipped')}
        aria-hidden="true"
      />
      <div
        className="ptour-spotlight"
        style={{
          left: rect.left - 6,
          top: rect.top - 6,
          width: rect.width + 12,
          height: rect.height + 12,
        }}
        aria-hidden="true"
      />
      <div
        ref={tooltipRef}
        className={`ptour-tooltip ptour-placement-${step.placement}`}
        style={{ left: tooltipPos.left, top: tooltipPos.top, width: TOOLTIP_W }}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          className="ptour-close"
          onClick={() => finish('skipped')}
          aria-label="Fermer le tutoriel"
        >
          <X size={14} aria-hidden="true" />
        </button>
        <div className="ptour-progress" aria-hidden="true">
          {STEPS.map((_, i) => (
            <span
              key={i}
              className={`ptour-progress-dot${i === stepIdx ? ' active' : ''}${i < stepIdx ? ' done' : ''}`}
            />
          ))}
        </div>
        <h3 id="ptour-title" className="ptour-title">
          {step.title}
        </h3>
        <p className="ptour-content">{step.content}</p>
        <div className="ptour-actions">
          <button
            type="button"
            className="ptour-btn ptour-btn-ghost"
            onClick={() => finish('skipped')}
          >
            Passer
          </button>
          <div className="ptour-actions-right">
            {!isFirst && (
              <button
                type="button"
                className="ptour-btn ptour-btn-secondary"
                onClick={() => setStepIdx((i) => Math.max(0, i - 1))}
                aria-label="Étape précédente"
              >
                <ChevronLeft size={14} aria-hidden="true" /> Retour
              </button>
            )}
            {isLast ? (
              <button
                type="button"
                className="ptour-btn ptour-btn-primary"
                onClick={() => finish('finished')}
              >
                Terminer
              </button>
            ) : (
              <button
                type="button"
                className="ptour-btn ptour-btn-primary"
                onClick={() => setStepIdx((i) => i + 1)}
              >
                Suivant <ChevronRight size={14} aria-hidden="true" />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
